import dayjs from "dayjs";
import { URGENCY_CFG } from "./pipeline";

// Column definitions shared between QuerysetV4's PDF export
// (generateQv4QueriesPdfBase64) and Excel export (generateQv4QueriesExcel)
// in misc_functions.js, plus the per-user "Customize PDF/Excel columns"
// picker in UserSettingsModal.js — kept in one place so both export formats
// and the settings UI never drift out of sync on what a column actually is.
// Order here matches QuerysetTable.js's own column order where a column
// overlaps with what's already visible in the table (query_id, created_at,
// agent, vehicle, parts, customer_name, phone, status); the rest are extra
// querysetV2 fields that exist in the dataset but aren't shown as their own
// table column today.
export const EXPORT_COLUMNS = [
  {
    key: "query_id",
    label: "Query ID",
    getValue: (r) => String(r.query_id ?? "N/A"),
  },
  {
    key: "created_at",
    label: "Created",
    getValue: (r) => {
      const d = r.created_at ? dayjs(r.created_at) : null;
      return d?.isValid() ? d.format("DD-MM-YYYY h:mm A") : "N/A";
    },
  },
  {
    key: "agent",
    label: "Agent",
    getValue: (r) => {
      const assignee = r.assigned_user;
      return assignee
        ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
        : "N/A";
    },
  },
  {
    key: "vehicle",
    label: "Vehicle",
    getValue: (r) => {
      const v = r.vehicle;
      return [v?.brand_name || v?.brand, v?.model_name || v?.model, v?.year ? `(${v.year})` : null]
        .filter(Boolean).join(" ") || "N/A";
    },
  },
  {
    key: "parts",
    label: "Parts",
    getValue: (r) => (r.query_parts || []).map(p => p.part_name).join(", ") || "N/A",
  },
  {
    key: "customer_name",
    label: "Customer Name",
    getValue: (r) => r.customer?.customer_name || r.customer_name || "N/A",
  },
  {
    key: "phone",
    label: "Phone",
    getValue: (r) => r.customer?.customer_mobile || r.customer_mobile || "N/A",
  },
  {
    key: "status",
    label: "Status",
    getValue: (r) => r.status || "N/A",
  },
  // Extra fields — present on every querysetV2 row but not currently
  // rendered as their own column in the table.
  {
    key: "urgency",
    label: "Urgency",
    getValue: (r) => URGENCY_CFG[r.urgency]?.label || "N/A",
  },
  {
    key: "remarks",
    label: "Remarks",
    getValue: (r) => r.remarks || "N/A",
  },
  {
    key: "customer_real_name",
    label: "Customer Real Name",
    getValue: (r) => r.customer?.customer_real_name || "N/A",
  },
  {
    key: "postponed_until",
    label: "Postponed Until",
    getValue: (r) => {
      const d = r.postponed_until ? dayjs(r.postponed_until) : null;
      return d?.isValid() ? d.format("DD-MM-YYYY h:mm A") : "N/A";
    },
  },
];

const DEFAULT_KEYS = EXPORT_COLUMNS.map(c => c.key);
const storageKey = (userId) => `qv4_export_columns_${userId || "anon"}`;

// All columns on by default (matches the export's pre-existing fixed
// column set) so an unvisited settings screen changes nothing.
export function loadExportColumnKeys(userId) {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey(userId)));
    if (!Array.isArray(stored) || !stored.length) return DEFAULT_KEYS;
    const valid = new Set(DEFAULT_KEYS);
    const filtered = stored.filter(k => valid.has(k));
    return filtered.length ? filtered : DEFAULT_KEYS;
  } catch {
    return DEFAULT_KEYS;
  }
}

export function saveExportColumnKeys(userId, keys) {
  localStorage.setItem(storageKey(userId), JSON.stringify(keys));
}

export function getEnabledExportColumns(userId) {
  const keys = new Set(loadExportColumnKeys(userId));
  return EXPORT_COLUMNS.filter(c => keys.has(c.key));
}
